/* ─────────────────────────────────────────────────────────────
   VALUATION CONFIG — Intrinsic Value tab

   Backs the three valuation endpoints added 2026-08-10:
     • GET /api/v1/stocks/{ticker}/intrinsic-value
     • GET /api/v1/stocks/{ticker}/valuation-history?limit=10
     • GET /api/v1/stocks/{ticker}/fair-value/custom?anchors=...

   All three are cached server-side for 15 minutes — the first request per
   ticker is the slow path, repeats are instant. So the anchor picker can call
   the custom endpoint on every selection change without being expensive.
───────────────────────────────────────────────────────────── */

/* ── Fair-value anchors (API 3) ──
   The stored fair value is the mean of the analyst target and the intrinsic
   value (e.g. AAPL 8 Oct: (341.68 + 145.77) / 2 = 243.73). Own-history FV
   (fv_historical) and Re-rate (fv_rerate) were removed from the page on
   2026-10-09 (Somya) — the API still serves them; the page never offers them. */
export const FV_ANCHORS = Object.freeze([
  {
    name: "fv_analyst",
    label: "Analyst target",
    inDefaultTrio: true,
    hint: "Mean of published analyst price targets.",
  },
  {
    name: "fv_iv",
    label: "True value",
    inDefaultTrio: true,
    hint: "DCF true value per share from the TV engine.",
  },
]);

export const DEFAULT_ANCHORS = Object.freeze(
  FV_ANCHORS.filter((a) => a.inDefaultTrio).map((a) => a.name)
);

export const anchorLabel = (name) =>
  FV_ANCHORS.find((a) => a.name === name)?.label ?? name;

/* ── Ratings-history factors ──
   NOTE the key here is `profitability`, NOT the `financial_profit` used by the
   screen-catalysts payload. balance_sheet_quality carries real grades in this
   series.                                                                   */
export const RATINGS_FACTOR_ROWS = Object.freeze([
  { key: "valuation", label: "Val" },
  { key: "growth", label: "Grw" },
  { key: "momentum", label: "Mom" },
  { key: "profitability", label: "Prof" },
  { key: "risk", label: "Risk" },
  { key: "balance_sheet_quality", label: "BSQ" },
]);

export const RATINGS_FACTOR_FULL_LABELS = Object.freeze({
  valuation: "Valuation",
  growth: "Growth",
  momentum: "Momentum",
  profitability: "Profitability",
  risk: "Risk",
  balance_sheet_quality: "Balance Sheet Quality",
});

/* ── confidence ∈ HIGH / MEDIUM / LOW ── */
export const confidenceTone = (c) =>
  ({ HIGH: "pos", MEDIUM: "warn", LOW: "neg" }[String(c ?? "").toUpperCase()] ?? "muted");

/* ══════════════════════════════════════════════════════════════
   THE FOUR VALUATION ENGINES

   Dispatch is automatic per ticker; `intrinsic_value.calculation_method`
   tells the FE which engine ran, and the header fields present differ
   slightly per engine. Shared by all: CAPM cost of equity (live rf +
   β × live Damodaran ERP, clamped 6–16%), per-name terminal growth,
   mid-year FYE-dated discounting with the first year pro-rated, diluted
   share count, and the continuation stage (5 fade years inserted when the
   pre-continuation terminal share exceeds 75%).
══════════════════════════════════════════════════════════════ */
export const ENGINES = Object.freeze({
  DCF_FCFE_PROJ: {
    label: "DCF on projected FCFE",
    short: "FCFE projection",
    who: "Default engine (industrials and most sectors)",
    fcfe: "OCF + CapEx (negative) + net borrowing",
    terminal: "Gordon growth on year-5 FCFE, plus continuation",
    extraFields: ["nb_method", "fy1_rem_frac"],
  },
  DCF_FCFE_BANK: {
    label: "DCF on bank FCFE",
    short: "Bank FCFE",
    who: "Financial Services where interest income is ≥ 45% of revenue",
    fcfe: "Net income − capital ratio × change in total assets",
    terminal: "NI5(1+g)(1−g/ROE5)/(r−g) — ROE-consistent payout",
    extraFields: ["cap_ratio", "roe5", "terminal_formula"],
  },
  NI_EXIT_MULTIPLE_AM: {
    label: "Net income × exit multiple",
    short: "Asset manager",
    who: "Asset managers (capital-light)",
    fcfe: "Net income (no capex or borrowing adjustment)",
    terminal: "Year-5 net income × exit multiple (market cap ÷ smoothed 3-yr NI)",
    extraFields: ["exit_multiple", "terminal_formula"],
  },
  DCF: {
    label: "DCF (historical fallback)",
    short: "Fallback DCF",
    who: "Used when the projection rows are unusable",
    fcfe: "Trailing free-cash-flow extrapolation",
    terminal: "Gordon growth",
    extraFields: [],
  },
});

export const engineMeta = (m) => ENGINES[m] ?? null;

export const methodLabel = (m) => ENGINES[m]?.label ?? m ?? null;

export const isFallbackMethod = (m) => m === "DCF";

export const isBankEngine = (m) => m === "DCF_FCFE_BANK";
export const isAssetManagerEngine = (m) => m === "NI_EXIT_MULTIPLE_AM";

/* `nb_method` — how net borrowing was projected. */
const NB_METHOD_LABELS = Object.freeze({
  history_ratio_decay: "history ratio, decaying",
  delta_projected_debt: "change in projected debt",
});

export const nbMethodLabel = (m) =>
  m ? NB_METHOD_LABELS[m] ?? String(m).replace(/_/g, " ") : null;

/* ── discounting convention ── */
const DISCOUNTING_LABELS = Object.freeze({
  mid_year_fye_dated_fy1_pro_rated:
    "mid-year convention, FYE-dated, first year pro-rated",
  mid_year_fye_dated: "mid-year convention, FYE-dated",
  mid_year: "mid-year convention",
  end_year: "end-of-year convention",
});

export const discountingLabel = (d) =>
  d ? DISCOUNTING_LABELS[d] ?? String(d).replace(/_/g, " ") : null;

/* ── data_as_of input names ── */
const INPUT_LABELS = Object.freeze({
  prices: "Prices",
  fundamentals: "Fundamentals",
  forecast: "Forecast",
  risk_free_rate: "Risk-free rate",
  equity_risk_premium: "Equity risk premium",
  analyst_consensus: "Analyst consensus",
  beta: "Beta",
});

export const inputLabel = (name) =>
  INPUT_LABELS[name] ??
  String(name)
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

/* ══════════════════════════════════════════════════════════════
   FORMATTERS specific to the valuation payloads
══════════════════════════════════════════════════════════════ */

const DASH = "—";

/**
 * Several valuation fields are DECIMALS that must render as percentages
 * (discount_rate 0.094 → 9.4 %, tv_share_of_value 0.7403 → 74.0 %),
 * while *_pct fields are ALREADY percentages and must not be multiplied.
 */
export const decimalAsPct = (v, decimals = 1) => {
  const n = Number(v);
  if (v == null || Number.isNaN(n)) return DASH;
  return `${(n * 100).toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}%`;
};

/**
 * Total DCF horizon, worded as the artifact words it:
 *   with continuation → "10-yr horizon: 5 continuation yrs added"
 *   without           → "5-yr explicit horizon"
 */
export const horizonLabel = (projectionYears, continuationYears) => {
  if (projectionYears == null) return null;
  const p = Number(projectionYears);
  const c = Number(continuationYears ?? 0);
  if (!c || Number.isNaN(c)) return `${p}-yr explicit horizon`;
  return `${p + c}-yr horizon: ${c} continuation yr${c === 1 ? "" : "s"} added`;
};

/** Artifact tooltip for the terminal-value-reliance card. */
export const TV_RELIANCE_TOOLTIP =
  "Share of the true value carried by the Gordon-growth perpetuity. Above ~75% the number is mostly a terminal-assumption bet; when that happens the engine automatically inserts 5 explicit continuation years (6–10, growth fading to terminal g) before the perpetuity.";

/**
 * `data_as_of` timestamps mix date-only ("2026-08-06") and full ISO strings.
 * Render date-only inputs without a misleading midnight time.
 */
export const fmtAsOf = (v) => {
  if (!v) return DASH;
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(String(v));
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  const datePart = d.toLocaleDateString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
  if (dateOnly) return datePart;
  return `${datePart}, ${d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}`;
};

/** Staleness in whole days, for flagging inputs that lag the valuation date. */
export const daysBetween = (a, b) => {
  const da = new Date(a);
  const db = new Date(b);
  if (Number.isNaN(da.getTime()) || Number.isNaN(db.getTime())) return null;
  return Math.floor(Math.abs(db - da) / 86400000);
};

/* ══════════════════════════════════════════════════════════════
   FCFE BUILD ROWS

   The table is TRANSPOSED (2026-09-09, per Somya): line items are rows and
   fiscal years are columns, matching the FCFE-build block in the reference
   note. `fcfe_history` rows (actuals) and `intrinsic_value.projected_fcfe`
   rows (forecast) still form ONE continuous left→right series of years.

   Only the build line items are shown — the model's working columns
   (ocf_anchored, ni_anchored, D&A, Δ WC, t (mid), organic vs anchored net
   income) are received but no longer rendered.

   kind: money  → compact currency
         pct    → decimal ×100
         years  → plain number (discount time in years)
         mult   → plain multiplier
══════════════════════════════════════════════════════════════ */
const FCFE_ROWS_PROJ = Object.freeze([
  { key: "revenue",       label: "Revenue",                     kind: "money" },
  { key: "ocf",           label: "Operating cash flow",         kind: "money" },
  { key: "capex",         label: "CapEx",                       kind: "money" },
  { key: "net_borrowing", label: "Net borrowing (Δ total debt)", kind: "money" },
  { key: "fcfe",          label: "FCFE",                        kind: "money", emphasis: true },
  { key: "pv",            label: "PV of FCFE",                  kind: "money" },
]);

const FCFE_ROWS_BANK = Object.freeze([
  { key: "net_income",    label: "Net income",                  kind: "money", note: "anchored" },
  { key: "asset_growth",  label: "Asset growth",                kind: "pct" },
  { key: "total_assets",  label: "Total assets",                kind: "money" },
  { key: "reinvest",      label: "Reinvestment",                kind: "money" },
  { key: "fcfe",          label: "FCFE",                        kind: "money", emphasis: true },
  { key: "pv",            label: "PV of FCFE",                  kind: "money" },
]);

const FCFE_ROWS_AM = Object.freeze([
  { key: "net_income",    label: "Net income",                  kind: "money" },
  { key: "fcfe",          label: "FCFE",                        kind: "money", emphasis: true, note: "= net income" },
  { key: "pv",            label: "PV of FCFE",                  kind: "money" },
]);

const FCFE_ROWS_FALLBACK = Object.freeze([
  { key: "ocf",           label: "Operating cash flow",         kind: "money" },
  { key: "capex",         label: "CapEx",                       kind: "money" },
  { key: "net_borrowing", label: "Net borrowing (Δ total debt)", kind: "money" },
  { key: "fcfe",          label: "FCFE",                        kind: "money", emphasis: true },
  { key: "pv",            label: "PV of FCFE",                  kind: "money" },
]);

/**
 * Line-item rows for the FCFE build, chosen by engine and then filtered to
 * those that actually carry a value in at least one fiscal year — so a name
 * whose payload omits a field never shows an all-dashes row.
 *
 * @param {string} method  intrinsic_value.calculation_method
 * @param {Array<object>} periods  the concatenated history + projected years
 */
export const fcfeRowsFor = (method, periods = []) => {
  const base =
    method === "DCF_FCFE_BANK"
      ? FCFE_ROWS_BANK
      : method === "NI_EXIT_MULTIPLE_AM"
      ? FCFE_ROWS_AM
      : method === "DCF"
      ? FCFE_ROWS_FALLBACK
      : FCFE_ROWS_PROJ;

  return base.filter((r) => periods.some((p) => p?.[r.key] != null));
};

/* ══════════════════════════════════════════════════════════════
   ANCHOR TREATMENTS  (GET .../intrinsic-value → anchor_treatments)

   The three ways the forecast anchor can be run, all priced on the initial
   GET — no extra /scenario request is needed to fill the dropdown or the
   range. `base` is byte-identical to intrinsic_value.iv_per_share, so the
   control can never disagree with the headline printed above it.

   NULL HANDLING, all three cases:
     • anchor_treatments null      → no anchor exists to vary (the
       asset-manager engine has none, and unanchored/fallback rows have
       nothing to hold or strip). Hide the control.
     • a treatment's iv_per_share null → that leg is not computable; show it
       unavailable, never as 0.
     • range                       → built only from the non-null legs.

   Never assume an ordering: no_anchor sits BELOW base on LMT (837.95 vs
   850.25) and above it on AMAT (171.63 vs 120.81). Take low/high from `range`.
══════════════════════════════════════════════════════════════ */
export const ANCHOR_TREATMENT_ORDER = Object.freeze(["base", "no_fade", "no_anchor"]);

/* Fallback labels — the payload ships its own `label`, which wins. */
const ANCHOR_TREATMENT_LABELS = Object.freeze({
  base: "Analyst consensus — EPS + revenue, then fade",
  no_fade: "Consensus held — no fade",
  no_anchor: "Off — the company's own model path",
});

/**
 * The /scenario body that reproduces a treatment. `base` is the stored result,
 * which the API defines as an empty body.
 */
export const anchorTreatmentBody = (key) => {
  if (key === "no_fade") return { fade: false };
  if (key === "no_anchor") return { consensus_anchor: false };
  return {};
};

/**
 * `anchor_treatments` → an ordered, render-ready list. Returns [] when the
 * block is null, which is the signal to hide the control entirely.
 *
 * @returns {Array<{key, label, iv_per_share, available}>}
 */
export const anchorTreatmentList = (anchorTreatments) => {
  if (!anchorTreatments) return [];
  return ANCHOR_TREATMENT_ORDER.map((key) => {
    const t = anchorTreatments[key];
    if (!t) return null;
    const iv = t.iv_per_share;
    return {
      key: t.key ?? key,
      label: t.label ?? ANCHOR_TREATMENT_LABELS[key] ?? key,
      iv_per_share: iv ?? null,
      available: iv != null,
    };
  }).filter(Boolean);
};

/**
 * Accuracy caveat for the no-fade leg. /scenario re-derives from the stored
 * projection and does not recompute the engine's calibrations, leaving an
 * interest correction whose inputs are not in the breakdown. Round-trip
 * residuals: LMT (fade_den 3.0) −0.07%, NVDA (5.41) −1.43%, AMAT (5.21)
 * −3.06%. So: don't print it to more precision than that, and never derive
 * margin of safety or upside from it — those stay on the base value.
 * `base` and `no_anchor` are exact and unaffected.
 */
export const NO_FADE_PRECISION_NOTE =
  "Held-consensus is re-derived from the stored projection rather than recalibrated, so it is good to roughly 1–3% on names whose anchor fades over more than three years. Margin of safety and upside stay on the base value.";

/** The no-fade caveat only bites once the anchor fades over more than 3 years. */
export const noFadeIsApproximate = (fadeDen) => Number(fadeDen ?? 0) > 3;

/* ══════════════════════════════════════════════════════════════
   CONSENSUS ANCHOR

   Years 1–2 net income is pinned to analyst FY1/FY2 consensus EPS × diluted
   shares (year-1 revenue to consensus revenue), then fades back to the model's
   own path. Momentum gates how long the anchor persists.
══════════════════════════════════════════════════════════════ */
export const FADE_DEN_LABELS = Object.freeze({
  6: {
    title: "Extended fade",
    detail:
      "Price is at least 1.08× its 200-day moving average and this is not a cyclical peak, so the anchor persists — the terminal year keeps half the year-2 anchor ratio.",
  },
  3: {
    title: "Full revert",
    detail: "The anchor is fully gone by the terminal year.",
  },
});

export const fadeDenLabel = (d) => FADE_DEN_LABELS[Number(d)] ?? null;

/* ══════════════════════════════════════════════════════════════
   SCENARIO ASSUMPTIONS  (POST .../intrinsic-value/scenario)

   Every key is optional; an empty body replays the stored engine result.
   `kind` drives the input: "pct" fields are entered as percentages and sent
   as decimals; "num" fields are sent as-is.
   `engines: null` → applies to every engine.
══════════════════════════════════════════════════════════════ */
export const SCENARIO_FIELDS = Object.freeze([
  {
    key: "risk_free_rate",
    label: "Risk-free rate",
    kind: "pct",
    engines: null,
    hint: "Rebuilds the cost of equity through CAPM.",
    storedFrom: (iv, inputs) => inputs?.risk_free_rate ?? null,
  },
  {
    key: "equity_risk_premium",
    label: "Equity risk premium",
    kind: "pct",
    engines: null,
    hint: "Rebuilds the cost of equity through CAPM.",
    storedFrom: (iv, inputs) => inputs?.equity_risk_premium ?? null,
  },
  {
    key: "beta",
    label: "Beta",
    kind: "num",
    engines: null,
    hint: "Rebuilds the cost of equity through CAPM.",
    storedFrom: (iv) => iv?.beta ?? null,
  },
  {
    key: "discount_rate",
    label: "Discount rate (CoE)",
    kind: "pct",
    engines: null,
    hint: "Sets the cost of equity directly — overrides the CAPM parts above.",
    storedFrom: (iv) => iv?.discount_rate ?? null,
  },
  {
    key: "terminal_growth",
    label: "Terminal growth",
    kind: "pct",
    engines: null,
    hint: "Gordon growth rate; the continuation stage is re-evaluated.",
    storedFrom: (iv) => iv?.terminal_growth_rate ?? null,
  },
  {
    key: "roe5",
    label: "ROE (year 5)",
    kind: "pct",
    engines: ["DCF_FCFE_BANK"],
    hint: "Bank engine only — drives the ROE-consistent terminal payout.",
    storedFrom: (iv) => iv?.roe5 ?? null,
  },
  {
    key: "exit_multiple",
    label: "Exit multiple",
    kind: "num",
    engines: ["NI_EXIT_MULTIPLE_AM"],
    hint: "Asset-manager engine only — applied to year-5 net income.",
    storedFrom: (iv) => iv?.exit_multiple ?? null,
  },
]);

/** Fields that apply to the engine that produced this valuation. */
export const scenarioFieldsFor = (method) =>
  SCENARIO_FIELDS.filter((f) => !f.engines || f.engines.includes(method));
